import React from 'react';
import { Button as AntButton } from 'antd';
import { Button } from '../button';
import { PlusOutlined, ExportOutlined } from '@ant-design/icons';
import { ModalManager } from '../modal-manager';
import style from './style.module.scss';

/*
    CreateButton
*/

interface CreateButtonProps {
    onClick: () => void | Promise<void>;
    loading?: boolean;
    disabled?: boolean;
    children?: React.ReactNode;
}

export function CreateButton({
    onClick,
    loading = false,
    disabled = false,
    children = 'Create new',
}: CreateButtonProps) {
    return (
        <Button
            onClick={onClick}
            loading={loading}
            variant="primary"
            icon={<PlusOutlined />}
            disabled={disabled}
        >
            {children}
        </Button>
    );
}

/*
    SaveButton
*/

interface SaveButtonProps {
    onClick?: () => void | Promise<void>;
    loading?: boolean;
    disabled?: boolean;
    children?: React.ReactNode;
    htmlType?: 'button' | 'submit' | 'reset';
    form?: string;
}

/**
 * SaveButton can either receive a onClick callback or a htmlType and form props if its bound to a form.
 */
export function SaveButton({
    onClick,
    loading = false,
    disabled = false,
    children = 'Save',
    htmlType,
    form,
}: SaveButtonProps) {
    return (
        <Button
            onClick={onClick}
            loading={loading}
            variant="primary"
            disabled={disabled}
            htmlType={htmlType}
            form={form}
        >
            {children}
        </Button>
    );
}

/*
    DeleteButton
*/

interface DeleteButtonProps {
    onClick: () => void | Promise<void>;
    loading?: boolean;
    disabled?: boolean;
    children?: React.ReactNode;
}

export function DeleteButton({
    onClick,
    loading = false,
    disabled = false,
    children = 'Delete',
}: DeleteButtonProps) {
    return (
        <Button
            variant="secondary-destructive"
            onClick={ModalManager.showDeleteModal({ onConfirm: onClick })}
            loading={loading}
            disabled={disabled}
        >
            {children}
        </Button>
    );
}

/*
    ExportButton
*/

interface ExportButtonProps {
    onClick: () => void | Promise<void>;
    loading?: boolean;
    disabled?: boolean;
    children?: React.ReactNode;
}

export function ExportButton({
    onClick,
    loading = false,
    disabled = false,
    children = 'Export',
}: ExportButtonProps) {
    return (
        <Button
            variant="secondary"
            icon={<ExportOutlined />}
            onClick={onClick}
            loading={loading}
            disabled={disabled}
        >
            {children}
        </Button>
    );
}

/*
    SquareButton with Icon
*/

interface SquareButtonProps {
    onClick?: () => void | Promise<void>;
    loading?: boolean;
    disabled?: boolean;
    icon?: React.ReactNode;
    type?: 'default' | 'primary' | 'dashed' | 'link' | 'text';
    className?: string;
    ariaLabel?: string;
}

export function SquareButton({
    onClick,
    loading = false,
    disabled = false,
    icon,
    type = 'default',
    className,
    ariaLabel,
}: SquareButtonProps) {
    // SquareButton uses types we don't support in our Button component (dashed, link)
    // So we use AntButton directly for this special case
    return (
        <AntButton
            type={type}
            onClick={onClick}
            loading={loading}
            disabled={disabled}
            icon={icon}
            aria-label={ariaLabel}
            className={
                className
                    ? `${style.square_button} ${className}`
                    : style.square_button
            }
            data-square-button="true"
        />
    );
}
